import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Subscription, SubscriptionPlan, ApiResponse } from '@millim/shared-types';

export interface ChangeSubscriptionDto {
  plan: SubscriptionPlan;
}

export interface ChangeSubscriptionResult {
  subscription: Subscription;
  action: 'upgraded' | 'queued';
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/subscriptions`;

  private readonly _subscriptions = signal<Subscription[]>([]);
  private readonly _loaded = signal(false);
  readonly subscriptions = this._subscriptions.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly active = computed(() => this._subscriptions().find((s) => s.status === 'active'));
  readonly features = computed<Record<string, boolean>>(
    () => (this.active()?.features as Record<string, boolean> | undefined) ?? {},
  );

  /** Clear cached subscription state (called on logout so the next
   * user's navbar doesn't momentarily render the previous user's plan). */
  reset(): void {
    this._subscriptions.set([]);
    this._loaded.set(false);
  }

  getSubscriptions(): Observable<ApiResponse<Subscription[]>> {
    return this.http.get<ApiResponse<Subscription[]>>(this.baseUrl).pipe(
      tap((res) => {
        if (res?.data) this._subscriptions.set(res.data);
        this._loaded.set(true);
      }),
    );
  }

  /** Allow components to override a flag locally (e.g. on a 403 from the backend). */
  setFeatureOverride(key: string, value: boolean): void {
    const current = this._subscriptions();
    const active = current.find((s) => s.status === 'active');
    if (!active) return;
    const updated = current.map((s) =>
      s === active ? { ...s, features: { ...(s.features ?? {}), [key]: value } } : s,
    );
    this._subscriptions.set(updated);
    this._loaded.set(true);
  }

  change(data: ChangeSubscriptionDto): Observable<ApiResponse<ChangeSubscriptionResult>> {
    return this.http.post<ApiResponse<ChangeSubscriptionResult>>(`${this.baseUrl}/change`, data);
  }
}
